import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, EnvelopeSimple, Phone, Globe, LinkedinLogo, MapPin, Clock,
  ChatCircleDots, Sparkle, ShieldCheck, PencilSimple, MagnifyingGlass,
} from "@phosphor-icons/react";

const STATUS_LABELS = {
  lead: "Lead",
  prospect: "Kans",
  active: "Actief",
  vip: "VIP",
  supplier: "Leverancier",
  investor: "Investeerder",
};

export default function CustomerDetail() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendPdfAttachment, setSendPdfAttachment] = useState(false);
  const [pdfAttachment, setPdfAttachment] = useState(null);
  const [pdfAttachmentLoading, setPdfAttachmentLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [mailMessage, setMailMessage] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [enrichingContacts, setEnrichingContacts] = useState(false);
  const [enrichmentMessage, setEnrichmentMessage] = useState("");

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setCustomer(r.data));
    api.get(`/customers/${id}/notes`).then((r) => setNotes(r.data));
  }, [id]);

  const health = useMemo(() => {
    if (!customer) return { score: 0, label: "Onbekend", issues: [] };
    const issues = [];
    if (!customer.email) issues.push("Geen e-mailadres");
    if (!customer.phone) issues.push("Geen telefoonnummer");
    if (!customer.next_followup) issues.push("Geen opvolgmoment");
    if (!customer.contacts?.length) issues.push("Geen contactpersonen");
    const score = Math.max(20, 100 - issues.length * 18);
    const label = score >= 80 ? "Sterk" : score >= 55 ? "Aandacht" : "Risico";
    return { score, label, issues };
  }, [customer]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    const { data } = await api.post(`/customers/${id}/notes`, { text: noteText });
    setNotes([data, ...notes]);
    setNoteText("");
  };

  const loadPdfAttachment = async () => {
    setPdfAttachmentLoading(true);
    try {
      const { data } = await api.get("/integrations/outlook/pdf-attachment");
      setPdfAttachment(data);
      setSendPdfAttachment(Boolean(data?.exists));
    } catch (error) {
      setPdfAttachment({
        exists: false,
        filename: null,
        error: error.response?.data?.detail || "PDF-status kon niet worden geladen.",
      });
    } finally {
      setPdfAttachmentLoading(false);
    }
  };

  const openEmail = () => {
    setEmailSubject(`Opvolging ${customer.company_name}`);
    setEmailBody("");
    setSendPdfAttachment(false);
    setMailMessage("");
    setEmailOpen(true);
    loadPdfAttachment();
  };

  const sendEmail = async () => {
    if (!customer.email) {
      setMailMessage("Deze klant heeft geen e-mailadres.");
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      setMailMessage("Vul een onderwerp en bericht in.");
      return;
    }

    setSending(true);
    setMailMessage("");
    try {
      const { data: mailResult } = await api.post("/integrations/outlook/send", {
        to: customer.email,
        subject: emailSubject,
        body: emailBody.replace(/\n/g, "<br />"),
        send_pdf_attachment: sendPdfAttachment,
      });
      setMailMessage(
        mailResult?.pdf_attached
          ? `E-mail verzonden met PDF: ${mailResult.pdf?.filename || "bijlage"}.`
          : sendPdfAttachment
            ? "E-mail verzonden, maar de server meldt dat er geen PDF is meegestuurd."
          : "E-mail verzonden."
      );
      if (!sendPdfAttachment) {
        setEmailSubject("");
        setEmailBody("");
      }
      const { data } = await api.get(`/customers/${id}`);
      setCustomer(data);
      if (!sendPdfAttachment) {
        setTimeout(() => setEmailOpen(false), 900);
      }
    } catch (error) {
      setMailMessage(error.response?.data?.detail || "E-mail verzenden is mislukt.");
    } finally {
      setSending(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      company_name: customer.company_name || "",
      status: customer.status || "lead",
      category: customer.category || "",
      email: customer.email || "",
      phone: customer.phone || "",
      website: customer.website || "",
      linkedin: customer.linkedin || "",
      country: customer.country || "",
      region: customer.region || "",
      vat_number: customer.vat_number || "",
      coc_number: customer.coc_number || "",
      billing_address: customer.billing_address || "",
      shipping_address: customer.shipping_address || "",
      notes: customer.notes || "",
      next_followup: customer.next_followup ? customer.next_followup.slice(0, 16) : "",
      sync_calendar: Boolean(customer.next_followup),
      contacts: customer.contacts || [],
      tags: customer.tags || [],
    });
    setEditMessage("");
    setEditOpen(true);
  };

  const updateEdit = (key, value) => setEditForm((current) => ({ ...current, [key]: value }));

  const saveCustomer = async () => {
    if (!editForm?.company_name?.trim()) return;
    setSavingCustomer(true);
    setEditMessage("");
    try {
      const payload = {
        ...editForm,
        next_followup: editForm.next_followup || null,
      };
      delete payload.sync_calendar;
      const { data } = await api.put(`/customers/${id}`, payload);

      if (editForm.sync_calendar && editForm.next_followup) {
        try {
          await api.post("/integrations/outlook/calendar-event", {
            customer_id: id,
            start_at: editForm.next_followup,
            subject: `Opvolging: ${editForm.company_name}`,
            duration_minutes: 30,
          });
        } catch (calendarError) {
          setEditMessage(
            calendarError.response?.data?.detail
            || "Klant opgeslagen, maar de agenda-afspraak kon nog niet worden aangemaakt. Controleer of de backend-update live staat."
          );
          setCustomer(data);
          return;
        }
      }

      setCustomer(data);
      setEditOpen(false);
    } catch (error) {
      setEditMessage(error.response?.data?.detail || "Klant opslaan is mislukt.");
    } finally {
      setSavingCustomer(false);
    }
  };

  const enrichContacts = async () => {
    setEnrichingContacts(true);
    setEnrichmentMessage("");

    try {
      const { data } = await api.post(`/customers/${id}/contact-enrichment`);
      setCustomer(data.customer);
      if (data.note) setNotes([data.note, ...notes]);

      const warnings = data.warnings?.length ? ` ${data.warnings.join(" ")}` : "";
      setEnrichmentMessage(
        data.configured === false
          ? "Contact Enrichment Agent is voorbereid, maar Hunter API key ontbreekt nog."
          : `${data.new_contacts?.length || 0} nieuwe contactpersonen gevonden.${warnings}`
      );
    } catch (error) {
      setEnrichmentMessage(
        error.response?.data?.detail || "Contactverrijking is mislukt."
      );
    } finally {
      setEnrichingContacts(false);
    }
  };

  if (!customer) return <div className="text-muted-foreground text-sm">Klant laden...</div>;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <Link to="/customers" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={14} /> Alle klanten
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">- Klantprofiel</div>
          <h1 className="font-display text-4xl tracking-tight leading-none">{customer.company_name}</h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <Badge variant="outline">{STATUS_LABELS[customer.status] || customer.status}</Badge>
            {customer.category && <Badge variant="outline" className="border-primary/40 text-primary">{customer.category}</Badge>}
            {customer.tags?.map((tag) => <Badge key={tag} variant="secondary">{tag}</Badge>)}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" data-testid="customer-edit-btn" onClick={openEdit}>
            <PencilSimple size={14} className="mr-1.5" /> Aanpassen
          </Button>
          <Button variant="outline" className="rounded-full" data-testid="customer-email-btn" onClick={openEmail}>
            <EnvelopeSimple size={14} className="mr-1.5" /> E-mail
          </Button>
          <Button variant="outline" className="rounded-full" data-testid="customer-enrich-contacts-btn" onClick={enrichContacts} disabled={enrichingContacts}>
            <MagnifyingGlass size={14} className="mr-1.5" /> {enrichingContacts ? "Zoeken..." : "Contacten zoeken"}
          </Button>
          <Button className="rounded-full bg-primary text-primary-foreground" data-testid="customer-ai-draft-btn">
            <Sparkle size={14} className="mr-1.5" /> AI concept
          </Button>
        </div>
      </div>

      {enrichmentMessage && (
        <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          {enrichmentMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList>
              <TabsTrigger value="overview">Overzicht</TabsTrigger>
              <TabsTrigger value="timeline">Tijdlijn</TabsTrigger>
              <TabsTrigger value="notes" data-testid="customer-notes-tab">Notities</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-5">
              <div className="rounded-xl border border-border bg-card p-6">
                <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                  <Info icon={EnvelopeSimple} label="E-mail" value={customer.email} />
                  <Info icon={Phone} label="Telefoon" value={customer.phone} />
                  <Info icon={Globe} label="Website" value={customer.website} />
                  <Info icon={LinkedinLogo} label="LinkedIn" value={customer.linkedin} />
                  <Info icon={MapPin} label="Land" value={customer.country} />
                  <Info icon={MapPin} label="Regio" value={customer.region} />
                  <Info label="BTW" value={customer.vat_number} />
                  <Info label="KvK" value={customer.coc_number} />
                  <Info label="Factuuradres" value={customer.billing_address} className="col-span-2" />
                  <Info label="Leveradres" value={customer.shipping_address} className="col-span-2" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="timeline" className="mt-5">
              <div className="rounded-xl border border-border bg-card p-6">
                <ul className="relative border-l border-border ml-2 space-y-5">
                  <TimelineItem icon={Clock} label="Aangemaakt" value={new Date(customer.created_at).toLocaleString("nl-NL")} />
                  {customer.last_contact && <TimelineItem icon={Clock} label="Laatste contact" value={new Date(customer.last_contact).toLocaleString("nl-NL")} />}
                  {customer.next_followup && <TimelineItem icon={Clock} label="Volgende opvolging" value={new Date(customer.next_followup).toLocaleString("nl-NL")} highlight />}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="mt-5">
              <div className="rounded-xl border border-border bg-card p-6 space-y-4">
                <div className="flex gap-3">
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Voeg een interne notitie toe..."
                    rows={2}
                    data-testid="customer-note-input"
                  />
                  <Button onClick={addNote} className="self-end" data-testid="customer-note-save">Toevoegen</Button>
                </div>
                <ul className="space-y-3">
                  {notes.map((note) => (
                    <li key={note.id} className="border-l-2 border-primary/40 pl-4 py-1">
                      <div className="text-sm">{note.text}</div>
                      <div className="text-xs text-muted-foreground mt-1">{note.author} - {new Date(note.created_at).toLocaleString("nl-NL")}</div>
                    </li>
                  ))}
                  {notes.length === 0 && <li className="text-sm text-muted-foreground">Nog geen notities.</li>}
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={15} weight="duotone" className="text-primary" />
              <div className="overline">Relatiegezondheid</div>
            </div>
            <div className="font-display text-3xl leading-none mt-3">{health.score}</div>
            <div className="text-sm text-muted-foreground mt-1">{health.label}</div>
            <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${health.score}%` }} />
            </div>
            <ul className="mt-4 space-y-2">
              {health.issues.length ? health.issues.map((issue) => (
                <li key={issue} className="text-xs text-muted-foreground">- {issue}</li>
              )) : <li className="text-xs text-muted-foreground">Geen directe aandachtspunten.</li>}
            </ul>
          </div>

          <div className="rounded-xl border border-border bg-card p-6">
            <div className="overline mb-1">Contacten</div>
            <h3 className="font-display text-lg tracking-tight mb-3">Belangrijke personen</h3>
            {customer.contacts?.length > 0 ? (
              <ul className="space-y-3">
                {customer.contacts.map((person, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <ChatCircleDots size={14} className="text-primary mt-1" />
                    <div>
                      <div className="text-sm">{person.name}</div>
                      <div className="text-xs text-muted-foreground">{person.title}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nog geen contactpersonen toegevoegd.</p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-6 ai-glow">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={14} weight="fill" className="text-primary" />
              <div className="overline">Zippe AI</div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Vraag de assistent om dit account samen te vatten, outreach te schrijven of relatiegezondheid te beoordelen.
            </p>
            <Link to="/ai" className="text-xs text-primary hover:underline mt-3 inline-block">Open AI Assistent -&gt;</Link>
          </div>
        </div>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>E-mail versturen</DialogTitle>
            <DialogDescription>
              Verstuur via de gekoppelde Outlook-mailbox. Het bericht wordt opgeslagen bij verzonden items.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="overline mb-1">Aan</div>
              <div className="text-sm">{customer.email || "Geen e-mailadres"}</div>
            </div>
            <div>
              <div className="overline mb-1">Onderwerp</div>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} data-testid="email-subject" />
            </div>
            <div>
              <div className="overline mb-1">Bericht</div>
              <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={8} data-testid="email-body" />
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border px-3 py-3">
              <div>
                <div className="text-sm font-medium">PDF meesturen</div>
                <div className="text-xs text-muted-foreground">
                  {pdfAttachmentLoading
                    ? "PDF op server controleren..."
                    : pdfAttachment?.exists
                      ? `${pdfAttachment.filename} (${pdfAttachment.size_kb} KB)`
                      : pdfAttachment?.error || "Nog geen PDF op de server gevonden."}
                </div>
              </div>
              <Switch
                checked={sendPdfAttachment}
                onCheckedChange={setSendPdfAttachment}
                disabled={!pdfAttachment?.exists}
                data-testid="send-pdf-attachment"
              />
            </div>
            {mailMessage && <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">{mailMessage}</div>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailOpen(false)}>Annuleren</Button>
            <Button onClick={sendEmail} disabled={sending || !customer.email} data-testid="send-email-btn">
              {sending ? "Versturen..." : "E-mail versturen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden p-0">
          <div className="px-6 pt-6 pb-4 border-b border-border">
            <DialogHeader>
              <DialogTitle>Klant aanpassen</DialogTitle>
              <DialogDescription>Werk relatiegegevens, categorie en opvolging bij.</DialogDescription>
            </DialogHeader>
          </div>
          {editForm && (
            <div className="grid grid-cols-2 gap-4 px-6 py-4 overflow-y-auto max-h-[calc(90vh-150px)]">
              <div className="col-span-2 space-y-1.5">
                <Label className="overline">Bedrijf *</Label>
                <Input value={editForm.company_name} onChange={(e) => updateEdit("company_name", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Status</Label>
                <Select value={editForm.status} onValueChange={(value) => updateEdit("status", value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Categorie</Label>
                <Input value={editForm.category} onChange={(e) => updateEdit("category", e.target.value)} placeholder="Bijv. Hotels, wasserij, leverancier" />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">E-mail</Label>
                <Input type="email" value={editForm.email} onChange={(e) => updateEdit("email", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Telefoon</Label>
                <Input value={editForm.phone} onChange={(e) => updateEdit("phone", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Website</Label>
                <Input value={editForm.website} onChange={(e) => updateEdit("website", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">LinkedIn</Label>
                <Input value={editForm.linkedin} onChange={(e) => updateEdit("linkedin", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Land</Label>
                <Input value={editForm.country} onChange={(e) => updateEdit("country", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Regio</Label>
                <Input value={editForm.region} onChange={(e) => updateEdit("region", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">BTW-nummer</Label>
                <Input value={editForm.vat_number} onChange={(e) => updateEdit("vat_number", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">KvK / bedrijfsnummer</Label>
                <Input value={editForm.coc_number} onChange={(e) => updateEdit("coc_number", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="overline">Volgende opvolging</Label>
                <Input type="datetime-local" value={editForm.next_followup} onChange={(e) => updateEdit("next_followup", e.target.value)} />
              </div>
              <label className="col-span-2 flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <span className="text-sm">Ook in Office 365 Agenda zetten</span>
                <Input
                  type="checkbox"
                  checked={editForm.sync_calendar}
                  onChange={(e) => updateEdit("sync_calendar", e.target.checked)}
                  className="h-4 w-4"
                />
              </label>
              <div className="col-span-2 space-y-1.5">
                <Label className="overline">Factuuradres</Label>
                <Textarea rows={2} value={editForm.billing_address} onChange={(e) => updateEdit("billing_address", e.target.value)} />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="overline">Leveradres</Label>
                <Textarea rows={2} value={editForm.shipping_address} onChange={(e) => updateEdit("shipping_address", e.target.value)} />
              </div>
              {editMessage && (
                <div className="col-span-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {editMessage}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-border bg-card">
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Annuleren</Button>
            <Button onClick={saveCustomer} disabled={savingCustomer}>{savingCustomer ? "Opslaan..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ icon: Icon, label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm flex items-center gap-2">
        {Icon && <Icon size={14} className="text-muted-foreground" />}
        <span>{value || "-"}</span>
      </div>
    </div>
  );
}

function TimelineItem({ icon: Icon, label, value, highlight }) {
  return (
    <li className="ml-4 relative">
      <span className={`absolute -left-[26px] top-1 w-3 h-3 rounded-full ${highlight ? "bg-primary" : "bg-muted-foreground/40"}`}></span>
      <div className="overline">{label}</div>
      <div className="text-sm flex items-center gap-1.5"><Icon size={12} className="text-muted-foreground" /> {value}</div>
    </li>
  );
}
